/**
 * PostVariant — User-generated feed post card.
 * Uses shared EngagementBar + ShareSection.
 * Unique: Author row, image gallery, ReactionsBar, comment/repost social row, edit/delete menu, follow button.
 */
import { useState, useRef, useMemo, useCallback, memo } from 'react';
import { MapPin, MessageCircle, UserPlus, UserCheck, Sparkles, Edit2, Trash2, MoreVertical, ChefHat, BadgeCheck, Crown, Repeat2, Award, Navigation2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFoodImage } from '../../hooks/useFoodImage';
import { useEngagement } from '../../hooks/useEngagement';
import { useFoodlist } from '../../contexts/FoodlistContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import ReactionsBar from '../ReactionsBar';
import AuthModal from '../AuthModal';
import EngagementBar from './EngagementBar';
import ShareSection from './ShareSection';
import { API_URL, timeAgo, buildShareHandler } from './shared';

const PostVariant = ({ post, onFoodlist, onFollow, onEdit, onDelete, onComment, followingIds = new Set(), currentUserId }) => {
  const { toast } = useToast();
  const primaryImg = post.image_url || post.images?.[0]?.url || '';
  const isRealPrimaryImg = primaryImg && primaryImg.startsWith('http');
  const { src: foodImg, source: foodImgSource, attribution: foodImgAttribution, handleError: handleFoodImgError } = useFoodImage(post.caption || post.venue_name, primaryImg, post.venue_name || '');
  const [imgSrc, setImgSrc] = useState(isRealPrimaryImg ? primaryImg : '');
  const [imgLoadFailed, setImgLoadFailed] = useState(false);
  // Only use imgSrc if it's a real URL and hasn't failed loading, otherwise use foodImg from hook
  // If both fail or are null, effectiveImgSrc will be falsy and we show directions placeholder
  const effectiveImgSrc = (!imgLoadFailed && imgSrc && imgSrc.startsWith('http')) ? imgSrc : foodImg;
  const [imgIdx, setImgIdx] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reposting, setReposting] = useState(false);
  const [reposted, setReposted] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isInFoodlist, toggleFoodlist } = useFoodlist();
  const touchRef = useRef({ startX: 0, startY: 0 });
  const images = post.images || [];
  const isOwn = post.user_id === currentUserId;
  const isFollowing = followingIds.has(post.user_id);
  const isVideo = post.media_type === 'video' || post.image_url?.includes('/video/');
  const tags = [...(post.tags || [])].slice(0, 5);

  const { repostCount, reposted: repostedLocal, shareCount, toggleRepost: toggleRepostLocal, recordShare } = useEngagement(
    post.caption || '', post.venue_name || '', post.post_id || '', user?.user_id || ''
  );
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [saveAnim, setSaveAnim] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [vibeDisliked, setVibeDisliked] = useState(false);

  const foodlistItem = useMemo(() => ({ name: post.venue_name || post.caption || 'Dish', restaurant: post.venue_name || '', location: post.city || '', image: post.image_url || '', cuisine: post.cuisine || '' }), [post]);
  const inFoodlist = isInFoodlist(foodlistItem.name, foodlistItem.restaurant);
  const [foodlistCount, setFoodlistCount] = useState(post.saves_count || 0);
  const testId = useMemo(() => (post.post_id || '').toString().slice(0, 20), [post.post_id]);

  const handleDelete = () => { if (confirmDelete) { onDelete?.(post.post_id); setConfirmDelete(false); setShowMenu(false); } else setConfirmDelete(true); };

  const handleRepost = async () => {
    if (reposted || reposting || isOwn) return;
    setReposting(true);
    setReposted(true); // Optimistic
    try {
      const token = localStorage.getItem('fq_auth_token');
      const res = await fetch(`${API_URL}/api/posts/repost`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, credentials: 'include', body: JSON.stringify({ post_id: post.post_id, comment: '' }) });
      if (!res.ok) setReposted(false); // Revert on error
    } catch {
      // Offline — queue via IndexedDB
      try {
        const { enqueue } = await import('../../lib/syncQueue');
        await enqueue({ path: '/api/posts/repost', method: 'POST', body: { post_id: post.post_id, comment: '' }, label: 'Repost' });
      } catch {}
    } finally { setReposting(false); }
  };

  const handleRepostLocal = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    toggleRepostLocal();
  }, [user, toggleRepostLocal]);

  const handleFoodlistToggle = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    toggleFoodlist(foodlistItem);
    if (!inFoodlist) { setSaveAnim(true); setFoodlistCount(c => c + 1); setTimeout(() => setSaveAnim(false), 800); }
    else { setFoodlistCount(c => Math.max(0, c - 1)); }
    onFoodlist?.(post);
  }, [user, foodlistItem, toggleFoodlist, inFoodlist, onFoodlist, post]);

  const handleFoodCardTap = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    navigate('/create-foodcard', { state: { prefill: { name: post.caption, restaurant: post.venue_name, cuisine: post.cuisine, image: post.image_url }, claimMode: true } });
  }, [user, post, navigate]);

  const handleNotMyVibe = useCallback(async () => {
    if (!user) { setShowAuthModal(true); return; }
    setVibeDisliked(true);
    try {
      await fetch(`${API_URL}/api/preferences/not-my-vibe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ item_name: post.caption || '', cuisine: post.cuisine || '', category: 'post', reason: 'not_interested', source: 'feed' }),
      });
    } catch {}
  }, [user, post]);

  const shareUrl = `${window.location.origin}/post/${post.post_id || ''}`;
  const baseShareHandler = useMemo(() => buildShareHandler(post.caption, post.venue_name, shareUrl), [post.caption, post.venue_name, shareUrl]);
  const handleSocialShare = useCallback(async (platform) => { await baseShareHandler(platform); recordShare(platform); if (platform === 'copy') toast({ title: 'Link copied!' }); }, [baseShareHandler, recordShare, toast]);

  return (
    <>
      <article className="group bg-white rounded-[22px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all duration-300" data-testid={`feed-card-${post.post_id || ''}`}>
        {post.is_promoted && (
          <div className="flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-[#FFF9F0] to-[#FFF5F0] border-b border-[#F4C430]/20">
            <Sparkles className="w-3 h-3 text-[#FF8C42]" /><span className="text-[10px] font-bold text-[#FF8C42] uppercase tracking-wide">Sponsored</span>
          </div>
        )}

        {/* Image */}
        {effectiveImgSrc ? (
        <div className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden cursor-pointer"
          onClick={() => post.post_id && navigate(`/post/${post.post_id}`)}
          onTouchStart={e => { touchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY }; }}
          onTouchEnd={e => {
            if (images.length <= 1) return;
            const dx = e.changedTouches[0].clientX - touchRef.current.startX;
            if (Math.abs(dx) > Math.abs(e.changedTouches[0].clientY - touchRef.current.startY) && Math.abs(dx) > 40) {
              const next = dx < 0 ? Math.min(imgIdx + 1, images.length - 1) : Math.max(imgIdx - 1, 0);
              if (next !== imgIdx) { setImgIdx(next); setImgSrc(images[next].url || ''); }
            }
          }}>
          {isVideo ? (
            <video src={effectiveImgSrc} controls playsInline preload="metadata" poster={post.thumb_url}
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" onError={() => { setImgSrc(''); setImgLoadFailed(true); }} />
          ) : (
            <img src={effectiveImgSrc} alt={post.caption || 'Food'}
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" onError={() => { setImgSrc(''); setImgLoadFailed(true); }} />
          )}
          {foodImgAttribution && <div className="absolute bottom-14 right-3 bg-black/50 backdrop-blur-md px-2 py-0.5 rounded-full z-10"><span className="text-white/90 text-[10px] font-medium">{foodImgAttribution}</span></div>}
          {images.length > 1 && (
            <>
              <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, i) => <button key={i} onClick={e => { e.stopPropagation(); setImgIdx(i); setImgSrc(images[i].url || ''); }} className={`h-1.5 rounded-full transition-all ${i === imgIdx ? 'bg-white w-5' : 'bg-white/55 w-1.5'}`} />)}
              </div>
              <span className="absolute top-2.5 right-2.5 bg-black/[0.32] border border-white/[0.12] text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full">{imgIdx + 1}/{images.length}</span>
            </>
          )}
          {claimed && <div className="absolute top-3 right-3 bg-emerald-500/90 backdrop-blur-md px-2.5 py-1 rounded-full flex items-center gap-1"><Award className="w-3 h-3 text-white" /><span className="text-white text-[10px] font-bold">Collected</span></div>}
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent h-24 pointer-events-none" />
          <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
            {post.vibe && <span className="bg-white/[0.14] backdrop-blur-md border border-white/[0.22] text-white text-[11px] font-medium px-2.5 py-1 rounded-[10px]">{post.vibe}</span>}
            {post.cuisine && <span className="bg-white/[0.14] backdrop-blur-md border border-white/[0.22] text-white text-[11px] font-medium px-2.5 py-1 rounded-[10px]">{post.cuisine}</span>}
            {post.venue_name && <span className="bg-white/[0.14] backdrop-blur-md border border-white/[0.22] text-white text-[11px] font-medium px-2.5 py-1 rounded-[10px] flex items-center gap-1 ml-auto"><MapPin className="w-3 h-3" />{post.venue_name}</span>}
          </div>
          {post.is_home_cooked && <span className="absolute top-3 left-3 bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-sm flex items-center gap-1"><ChefHat className="w-3 h-3" /> Home-Cooked</span>}
        </div>
        ) : (
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((post.venue_name || '') + ' ' + (post.location || post.city || ''))}`}
            target="_blank" rel="noopener noreferrer"
            className="relative aspect-[4/3] md:aspect-[16/10] bg-gradient-to-br from-slate-100 via-slate-50 to-orange-50 flex flex-col items-center justify-center cursor-pointer hover:from-slate-200 hover:to-orange-100 transition-all" data-testid={`directions-post-${post.post_id}`}>
            <div className="w-14 h-14 rounded-full bg-[#FF6B35]/10 flex items-center justify-center mb-2">
              <Navigation2 className="w-7 h-7 text-[#FF6B35]" />
            </div>
            <p className="text-sm font-bold text-gray-800">{post.venue_name || post.caption}</p>
            {post.location && <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{post.location}</p>}
            <div className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-[#FF6B35] text-white rounded-full text-xs font-semibold">
              <Navigation2 className="w-3.5 h-3.5" /> Get Directions
            </div>
          </a>
        )}

        {/* Card Body */}
        <div className="p-4 md:p-5">
          {/* Author Row */}
          <div className="flex items-center gap-3 mb-3">
            <img src={post.user_avatar || `https://i.pravatar.cc/150?u=${post.user_id}`} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100" onError={e => { e.target.src = `https://i.pravatar.cc/150?u=${post.user_id || 'default'}`; }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-[15px] text-gray-900 truncate">{post.user_name || 'Foodie'}</span>
                {post.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                {post.is_premium && <Crown className="w-4 h-4 text-[#F4C430] flex-shrink-0" />}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                {post.city && <span>{post.city}</span>}
                {post.created_at && <><span>·</span><span>{timeAgo(post.created_at)}</span></>}
              </div>
            </div>
            {isOwn ? (
              <div className="relative flex-shrink-0">
                <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors" data-testid="post-menu-btn"><MoreVertical className="w-4 h-4" /></button>
                {showMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => { setShowMenu(false); setConfirmDelete(false); }} />
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden min-w-[140px]">
                      {onEdit && <button onClick={() => { onEdit(post); setShowMenu(false); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-testid="edit-post-btn"><Edit2 className="w-4 h-4" />Edit Post</button>}
                      {onDelete && <button onClick={handleDelete} className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 ${confirmDelete ? 'bg-red-500 text-white hover:bg-red-600' : 'text-red-500 hover:bg-red-50'}`} data-testid="delete-post-btn"><Trash2 className="w-4 h-4" />{confirmDelete ? 'Confirm Delete' : 'Delete Post'}</button>}
                    </div>
                  </>
                )}
              </div>
            ) : post.user_id ? (
              <button onClick={() => onFollow(post.user_id)} className={`flex items-center gap-1 px-2.5 py-1 rounded-2xl text-[11px] font-semibold transition-all flex-shrink-0 ${isFollowing ? 'bg-[#FFF5F0] text-[#FF6B35] border border-[#FF6B35]/25' : 'bg-gray-100 text-gray-600 hover:bg-[#FFF5F0] hover:text-[#FF6B35]'}`}>
                {isFollowing ? <UserCheck className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}{isFollowing ? 'Following' : 'Follow'}
              </button>
            ) : null}
          </div>

          {post.caption && <p className="text-[15px] text-gray-800 leading-relaxed mb-3 line-clamp-3">{post.caption}</p>}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {tags.map(tag => <button key={tag} onClick={() => navigate(`/foodie?tag=${encodeURIComponent(tag)}`)} className="text-xs text-[#FF6B35] font-medium hover:underline cursor-pointer" data-testid={`tag-pill-${tag}`}>#{tag}</button>)}
            </div>
          )}

          <EngagementBar repostCount={repostCount} onRepost={handleRepostLocal} reposted={repostedLocal}
            onNotMyVibe={handleNotMyVibe} vibeDisliked={vibeDisliked}
            inFoodlist={inFoodlist} foodlistCount={foodlistCount} onFoodlistToggle={handleFoodlistToggle} saveAnim={saveAnim}
            onFoodCardTap={handleFoodCardTap} claimed={claimed} claimLoading={claimLoading} testId={testId} />

          {/* Social Row: Reactions · Comment · Repost */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100/80 mt-2">
            <ReactionsBar postId={post.post_id || ''} />
            <button onClick={() => onComment?.(post)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-gray-400 hover:bg-gray-50 hover:text-blue-500 transition-colors" data-testid="comment-btn">
              <MessageCircle className="w-[18px] h-[18px]" />{(post.comments_count || 0) > 0 && <span className="text-xs font-medium">{post.comments_count}</span>}
            </button>
            <button onClick={handleRepost} disabled={isOwn || reposting}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${reposted ? 'text-emerald-500 bg-emerald-50' : 'text-gray-400 hover:bg-gray-50 hover:text-emerald-500'} ${isOwn ? 'opacity-30 cursor-not-allowed' : ''}`} data-testid="repost-btn">
              <Repeat2 className={`w-[18px] h-[18px] ${reposting ? 'animate-spin' : ''}`} />{(post.repost_count || 0) > 0 && <span className="text-xs font-medium">{(post.repost_count || 0) + (reposted ? 1 : 0)}</span>}
            </button>
          </div>

          <ShareSection onSocialShare={handleSocialShare} shareCount={shareCount} testId={testId} compact />
        </div>
      </article>
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </>
  );
};

const MemoizedPostVariant = memo(PostVariant);
MemoizedPostVariant.displayName = 'PostVariant';
export default MemoizedPostVariant;
